import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { CHARGE_CATS } from './constants';

// ── Tiny sub-components used by the budget tab ──

export const InlineAdd = ({ placeholder, onAdd, color, testId }) => (
  <div className="flex gap-2">
    <Input placeholder={placeholder} className="bg-cap-bg border-white/10 text-white text-sm" id={`ia-${testId}-n`} data-testid={`${testId}-name`} />
    <Input type="number" placeholder="€" className="bg-cap-bg border-white/10 text-white text-sm w-24" id={`ia-${testId}-a`} data-testid={`${testId}-amt`} />
    <Button
      size="sm"
      onClick={() => {
        const n = document.getElementById(`ia-${testId}-n`).value;
        const a = document.getElementById(`ia-${testId}-a`).value;
        if (n && a) { onAdd(n, a); document.getElementById(`ia-${testId}-n`).value = ''; document.getElementById(`ia-${testId}-a`).value = ''; }
      }}
      className={`bg-cap-${color}/20 text-cap-${color} hover:bg-cap-${color}/30 shrink-0`}
      data-testid={`add-${testId}`}
    >
      <Plus className="w-4 h-4" />
    </Button>
  </div>
);

export const ChargeAdd = ({ onAdd, color, testId, allCats, onAddCat, onRemoveCat }) => {
  const [cat, setCat] = useState('');
  const cats = allCats || CHARGE_CATS;
  const handleCatChange = (v) => {
    if (v === '__add_cat__') {
      const name = prompt('Nom de la nouvelle catégorie :');
      if (name && onAddCat) { const created = onAddCat(name); if (created) setCat(created); }
    } else { setCat(v); }
  };
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input placeholder="Nom" className="bg-cap-bg border-white/10 text-white text-sm" id={`ca-${testId}-n`} data-testid={`${testId}-name`} />
        <Input type="number" placeholder="€" className="bg-cap-bg border-white/10 text-white text-sm w-20" id={`ca-${testId}-a`} data-testid={`${testId}-amt`} />
      </div>
      <div className="flex gap-2">
        <Select value={cat} onValueChange={handleCatChange}>
          <SelectTrigger className="bg-cap-bg border-white/10 text-white text-sm"><SelectValue placeholder="Catégorie" /></SelectTrigger>
          <SelectContent className="bg-cap-bg-elevated border-white/10">
            {cats.map(c => (
              <SelectItem key={c} value={c} className="group">
                <div className="flex items-center justify-between w-full">
                  {c}
                  {onRemoveCat && !CHARGE_CATS.includes(c) && (
                    <span
                      onPointerDown={e => { e.stopPropagation(); e.preventDefault(); onRemoveCat(c); }}
                      className="opacity-0 group-hover:opacity-100 ml-2 text-cap-text-muted hover:text-cap-error transition-opacity cursor-pointer"
                    >
                      <Trash2 className="w-3 h-3" />
                    </span>
                  )}
                </div>
              </SelectItem>
            ))}
            <SelectItem value="__add_cat__"><span className="text-cap-emerald">+ Créer une catégorie</span></SelectItem>
          </SelectContent>
        </Select>
        <Button
          size="sm"
          onClick={() => {
            const n = document.getElementById(`ca-${testId}-n`).value;
            const a = document.getElementById(`ca-${testId}-a`).value;
            if (n && a) { onAdd(n, a, cat || 'Autres'); document.getElementById(`ca-${testId}-n`).value = ''; document.getElementById(`ca-${testId}-a`).value = ''; setCat(''); }
          }}
          className={`bg-${color}-500/20 text-${color}-400 hover:bg-${color}-500/30 shrink-0`}
          data-testid={`add-${testId}`}
        >
          <Plus className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

export const EnvForm = ({ onAdd }) => {
  const [type, setType] = useState('');
  return (
    <div className="flex gap-2 flex-wrap">
      <Input placeholder="Nom (ex: PEA)" className="bg-cap-bg border-white/10 text-white text-sm flex-1 min-w-[100px]" id="ef-n" data-testid="env-name" />
      <Input type="number" placeholder="€" className="bg-cap-bg border-white/10 text-white text-sm w-20" id="ef-a" data-testid="env-amt" />
      <Select value={type} onValueChange={setType}>
        <SelectTrigger className="bg-cap-bg border-white/10 text-white text-sm w-36" data-testid="env-type"><SelectValue placeholder="Type" /></SelectTrigger>
        <SelectContent className="bg-cap-bg-elevated border-white/10">
          <SelectItem value="epargne">Épargne</SelectItem>
          <SelectItem value="investissement">Investissement</SelectItem>
          <SelectItem value="provision">Provision</SelectItem>
        </SelectContent>
      </Select>
      <Button
        size="sm"
        onClick={() => {
          const n = document.getElementById('ef-n').value;
          const a = document.getElementById('ef-a').value;
          if (n && a && type) { onAdd({ name: n, amount: parseFloat(a), type }); document.getElementById('ef-n').value = ''; document.getElementById('ef-a').value = ''; setType(''); }
          else toast.error('Remplissez tous les champs');
        }}
        className="bg-cap-blue/20 text-cap-blue hover:bg-cap-blue/30 shrink-0"
        data-testid="add-env"
      >
        <Plus className="w-4 h-4" />
      </Button>
    </div>
  );
};

// ── New envelope form for "Mon Épargne" tab ──
export const NewEnvelopeForm = ({ onAdd }) => {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState('');
  if (!open) return (
    <button onClick={() => setOpen(true)} className="w-full glass-card p-4 flex items-center justify-center gap-2 text-cap-text-muted hover:text-cap-emerald hover:border-cap-emerald/20 transition-all" data-testid="new-env-btn">
      <Plus className="w-4 h-4" /> <span className="text-sm font-medium">Nouvelle enveloppe</span>
    </button>
  );
  return (
    <div className="glass-card p-5 space-y-3" data-testid="new-env-form">
      <h4 className="text-white text-sm font-semibold">Nouvelle enveloppe</h4>
      <div className="grid grid-cols-2 gap-3">
        <Input placeholder="Nom (ex: Livret A)" className="bg-cap-bg border-white/10 text-white text-sm" id="ne-name" data-testid="ne-name" />
        <Input type="number" placeholder="Montant cible / mois" className="bg-cap-bg border-white/10 text-white text-sm" id="ne-amt" data-testid="ne-amt" />
      </div>
      <Select value={type} onValueChange={setType}>
        <SelectTrigger className="bg-cap-bg border-white/10 text-white text-sm" data-testid="ne-type"><SelectValue placeholder="Type d'enveloppe" /></SelectTrigger>
        <SelectContent className="bg-cap-bg-elevated border-white/10">
          <SelectItem value="epargne">Épargne</SelectItem>
          <SelectItem value="investissement">Investissement</SelectItem>
          <SelectItem value="provision">Provision</SelectItem>
        </SelectContent>
      </Select>
      <div className="flex gap-2">
        <Button
          onClick={() => {
            const n = document.getElementById('ne-name').value;
            const a = document.getElementById('ne-amt').value;
            if (n && a && type) { onAdd({ name: n, amount: parseFloat(a), type }); setOpen(false); setType(''); }
            else { toast.error('Remplissez tous les champs'); }
          }}
          className="bg-cap-emerald text-cap-bg font-semibold hover:bg-cap-emerald-hover flex-1"
          data-testid="ne-submit"
        >
          Créer
        </Button>
        <Button variant="ghost" onClick={() => setOpen(false)} className="text-cap-text-muted">Annuler</Button>
      </div>
    </div>
  );
};
